import './DateTimeEntryContent.css';

import { useState } from 'react';

import { useModalKeyboard } from '../../../../../hooks/shortcuts/useModalKeyboard';
import {
  DateTimeInputControl,
  FilterError,
  TimeInputControl,
} from '../../../../../types/types';
import DateTimePicker from '../../../../Manager/GridInput/components/DateTimePicker/DateTimePicker';
import TimePicker from '../../../../Manager/GridInput/components/TimePicker/TimePicker';
import { parseEntryDefaultDate } from '../../utils/parseEntryDefaultDate';
import EntryActionButtons from '../EntryActionButtons/EntryActionButtons';

export interface DateTimeEntryContentProps {
  valueType: 'date' | 'time' | 'datetime';
  defaultValue?: string | number;
  isSubmitting: boolean;
  handleAction: (action: () => void | Promise<void>) => void | Promise<void>;
  submitEntryValue: (value: string) => void | Promise<void>;
  cancelModalAction: () => void | Promise<void>;
}

// Entry errors carry no dataset control definition — min/max are simply unset.
const ENTRY_DATETIME_CONTROL: DateTimeInputControl = { type: 'dateTime' };
const ENTRY_TIME_CONTROL: TimeInputControl = { type: 'time' };

const DateTimeEntryContent = ({
  valueType,
  defaultValue,
  isSubmitting,
  handleAction,
  submitEntryValue,
  cancelModalAction,
}: DateTimeEntryContentProps) => {
  // Pickers emit the value already in the API format: yyyy-MM-dd (date),
  // HH:mm:00 (time) or yyyy-MM-dd'T'HH:mm:00 (datetime).
  const [value, setValue] = useState<string>(() =>
    parseEntryDefaultDate(valueType, defaultValue)
  );
  const [error, setError] = useState<FilterError>(null);

  const isValid = value !== '' && error === null;

  const submit = () => {
    if (!isValid) {
      return;
    }

    handleAction(() => submitEntryValue(value));
  };

  useModalKeyboard({
    onEnter: isValid ? submit : undefined,
    onEscape: () => handleAction(cancelModalAction),
  });

  const pickerProps = {
    name: 'entry-error-value',
    value: value === '' ? null : value,
    onChange: (newValue: string) => setValue(newValue),
    error,
    setError,
    disabled: isSubmitting,
  };

  return (
    <div className="datetime-entry-content">
      {valueType === 'time' ? (
        <TimePicker {...pickerProps} control={ENTRY_TIME_CONTROL} />
      ) : (
        <DateTimePicker
          {...pickerProps}
          control={ENTRY_DATETIME_CONTROL}
          dateOnly={valueType === 'date'}
          // Forces the time section on (checkbox hidden) for datetime entries.
          isFormInput={valueType === 'datetime'}
        />
      )}
      <EntryActionButtons
        onConfirm={submit}
        confirmDisabled={!isValid}
        onCancel={() => handleAction(cancelModalAction)}
      />
    </div>
  );
};

export default DateTimeEntryContent;
